import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Timestamp } from '@britishcouncil/common-core';
import { useCallback, useEffect, useMemo } from 'react';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { speakingSessionStore } from 'store';
import { useIntParam, useSafeMutation, useServerErrors } from 'hooks';
import { parseServerErrors } from 'utils';
import { ROUTING_PATHS } from 'const';
import { useSpeakingSessionDetails } from '../details/useSpeakingSessionDetails';
import { EditSpeakingSessionFormValues, schema } from './validation';

export const useEditSpeakingSession = () => {
  const sessionId = useIntParam('sessionId');
  const navigate = useNavigate();
  const location = useLocation();
  const { speakingSessionDetails } = ROUTING_PATHS;
  const { speakingSession, isLoading: isFetchingSession } = useSpeakingSessionDetails();

  const form = useForm<EditSpeakingSessionFormValues>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {},
  });

  useEffect(() => {
    if (!speakingSession) {
      return;
    }

    form.reset({
      sessionDate: Timestamp.fromDate(new Date(speakingSession.date)),
      testTakerAdmissionTimeMinutes: speakingSession.testTakerAdmissionTimeMinutes,
    });
  }, [speakingSession, form]);

  const [updateSpeakingSession, { isLoading: isSaving, error: updateError }] =
    speakingSessionStore.service.useUpdateSpeakingSessionMutation();

  const goToSpeakingSessionDetails = useCallback(
    () =>
      navigate(generatePath(speakingSessionDetails.absolutePath, { sessionId }), {
        state: location.state,
      }),
    [navigate, speakingSessionDetails.absolutePath, sessionId, location.state]
  );

  const { execute: executeUpdateSpeakingSession } = useSafeMutation(updateSpeakingSession, {
    successToastMessage: 'Changes saved successfully',
    onSuccess: goToSpeakingSessionDetails,
  });

  const serverErrors = useMemo(() => parseServerErrors(updateError), [updateError]);

  const { generalErrors, clearServerErrors } = useServerErrors(
    form.getValues(),
    serverErrors,
    form.setError
  );

  const handleSubmit = async (data: EditSpeakingSessionFormValues) => {
    clearServerErrors();

    await executeUpdateSpeakingSession({
      speakingSessionId: sessionId,
      request: {
        testTakerAdmissionTimeMinutes: data.testTakerAdmissionTimeMinutes ?? 0,
      },
    });
  };

  return {
    form,
    handleSubmit,
    generalErrors,
    venue: speakingSession?.venue ?? '',
    isFetchingSession,
    isSaving,
  };
};
